import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Archive } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Archive conversation — soft-delete replacement for the retired DELETE
 * endpoint. Reversible, so no typing gate: a single click on "Archivar"
 * confirms. Reason is optional and stored on the doc for audit.
 *
 * Backend safeguards (only role=admin) are still enforced; the caller also
 * hides the button for non-admins so a viewer never sees this dialog.
 */
export default function ArchiveConversationDialog({
    open, onOpenChange, conversation, trip, customer, onArchived,
}) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (open) setReason(""); }, [open]);

    const tripTitle = trip?.title || trip?.external_refs?.kimkim_trip_slug || "—";
    const customerName = customer?.name || "—";

    const onSubmit = async () => {
        if (!conversation) return;
        // Bug fix 07-Aug-2026 — the conversation object we receive here is
        // `selectedDetail.conversation` which comes STRAIGHT from Mongo
        // (`_id` as key) via GET /conversations/{id} (see server.py
        // conversation_detail). The list endpoint normalises to `id` but
        // the detail endpoint keeps `_id`. Previously we read `.id` and
        // sent POST /conversations/undefined/archive → 404 → the dialog
        // silently failed and the row appeared unchanged in the list.
        // Accept both so the code is robust to either shape.
        const convId = conversation.id || conversation._id;
        if (!convId) {
            toast.error("No se pudo identificar la conversación");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `/conversations/${convId}/archive`,
                reason.trim() ? { reason: reason.trim() } : {},
            );
            toast.success("Conversación archivada");
            onArchived?.(convId, data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo archivar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="archive-conversation-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                            <Archive size={18} weight="bold" />
                        </div>
                        <div>
                            <DialogTitle>Archivar conversación</DialogTitle>
                            <DialogDescription>
                                Se ocultará del inbox principal pero podrás recuperarla desde el filtro «Archivadas». No se pierden mensajes ni datos.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700"
                     data-testid="archive-conv-summary">
                    <div><span className="text-slate-500">Trip:</span> <strong>{tripTitle}</strong></div>
                    <div><span className="text-slate-500">Cliente:</span> <strong>{customerName}</strong></div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="archive-reason">Motivo (opcional)</Label>
                    <Input
                        id="archive-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Ej: viaje finalizado, spam, duplicada…"
                        maxLength={200}
                        data-testid="archive-conv-reason"
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="archive-conv-cancel">
                        Cancelar
                    </Button>
                    <Button
                        onClick={onSubmit}
                        disabled={submitting}
                        className="bg-brand-600 hover:bg-brand-700"
                        data-testid="archive-conv-confirm"
                    >
                        {submitting ? "Archivando…" : "Archivar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
